import React, { useState, useEffect, useRef } from 'react';
import CustomLoading from '../../../util_components/custom_loading';

function TrialSession({
  close_availability_graph,
  teacherName,
  teacherPointsData,
  teacherPointsEntries,
  onClearResults,
  pointsReason,
  onChange,
  pointsValue,
  AddupdatePoints,
  RemoveupdatePoints,
  validationError,
  pointsOnchange,
  onFilterSubmit,
  currentPage,
  totalPages,
  fetchStudents
}) {
  const tdStyle = 'border border-[var(--border-default)] p-2';
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    month: ''
  });

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentList, setStudentList] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [localReason, setLocalReason] = useState(pointsReason || '');
  const [errorMsg, setErrorMsg] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'month' && value) {
      setFilters((prev) => ({ ...prev, [name]: value, startDate: '', endDate: '' }));
    } else if ((name === 'startDate' || name === 'endDate') && value) {
      setFilters((prev) => ({ ...prev, [name]: value, month: '' }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
    if (name === 'search') {
      setSelectedStudent(null);
      setShowDropdown(false);
    }
  };

  const filteredStudentList = studentList;

  const handleSearch = async () => {
    if (selectedStudent) {
      const params = { ...filters, student_uuid: selectedStudent.student_uuid };
      onFilterSubmit(params);
      setShowDropdown(false);
      return;
    }

    if (filters.search.trim()) {
      setLoadingStudents(true);
      setShowDropdown(true);

      const data = await fetchStudents(filters.search.trim());
      setStudentList(data);
      setLoadingStudents(false);
      return;
    }

    const params = { ...filters };
    onFilterSubmit(params);
    setShowDropdown(false);
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setFilters((prev) => ({
      ...prev,
      search: `${student.first_name} ${student.last_name || ''}`.trim()
    }));
    setShowDropdown(false);
  };

  const handlePageChange = (newPage) => {
    onFilterSubmit({ ...filters, page: newPage });
  };

  const handleSaveReason = () => {
    if (!localReason.trim()) {
      setErrorMsg('Please enter a reason before saving.');
      return;
    }
    onChange({ target: { value: localReason } });
    setShowReasonPopup(false);
    setErrorMsg('');
  };

  const handlePointsChange = (e) => {
    let val = e.target.value.toString();

    val = val.replace(/\D/g, '');
    if (val.length > 3) val = val.slice(0, 3);
    const numeric = val === '' ? '' : Number(val);
    pointsOnchange({ target: { value: numeric } });
    if (errorMsg && numeric > 0) setErrorMsg('');
  };

  const handlePointsPaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    if (!/^[0-9]+$/.test(paste)) {
      e.preventDefault();
      return;
    }
    if (paste.length > 3) {
      const clamped = paste.slice(0, 3);
      pointsOnchange({ target: { value: Number(clamped) } });
      e.preventDefault();
    }
  };

  const handleAddPoints = () => {
    if (!pointsReason?.trim()) return setErrorMsg('Enter a reason.');
    const val = Number(pointsValue);
    if (!pointsValue || val <= 0) return setErrorMsg('Enter valid points.');
    if (val > 999) return setErrorMsg('Maximum allowed points is 999.');
    setErrorMsg('');
    AddupdatePoints(selectedStudent?.student_uuid);
    setLocalReason('');
    if (onChange) onChange({ target: { value: '' } });
  };

  const handleRemovePoints = () => {
    if (!pointsReason?.trim()) return setErrorMsg('Enter a reason.');
    const val = Number(pointsValue);
    if (!pointsValue || val <= 0) return setErrorMsg('Enter valid points.');
    if (val > 999) return setErrorMsg('Maximum allowed points is 999.');
    setErrorMsg('');
    RemoveupdatePoints(selectedStudent?.student_uuid);
    setLocalReason('');
    if (onChange) onChange({ target: { value: '' } });
  };

  return (
    <div className="common-page-container">
      <div className="common-page-header">
        <div className="profile-go-back" onClick={close_availability_graph}>
          <img
            src="https://s3.amazonaws.com/images.myyogateacher.com/website/home/arrow_back.png"
            alt="back"
          />
          <span>Back</span>
        </div>
        <div className="common-page-title">
          <h2 className="font-extrabold">Trial Session Points</h2>
        </div>
      </div>
      {/* Filters */}
      <div className="mt-5 flex items-center flex-wrap gap-3">
        {['startDate', 'endDate', 'month'].map((f) => (
          <div key={f}>
            <label>
              {f === 'month' ? 'Month:' : f === 'startDate' ? 'Start Date:' : 'End Date:'}
            </label>
            <input
              type={f === 'month' ? 'month' : 'date'}
              name={f}
              value={filters[f]}
              onChange={handleFilterChange}
              className="border p-1 rounded cursor-pointer"
              onFocus={(e) => e.target.showPicker && e.target.showPicker()}
            />
          </div>
        ))}

        <div style={{ position: 'relative' }}>
          <label>Search Student:</label>
          <input
            type="text"
            name="search"
            value={filters.search}
            placeholder="Search by name"
            onChange={handleFilterChange}
            className="border p-1 rounded"
            autoComplete="off"
          />
          {showDropdown && (
            <div
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 10,
                background: 'var(--surface-card)',
                border: '1px solid var(--border-default)',
                borderRadius: '4px',
                maxHeight: '220px',
                overflowY: 'auto'
              }}
            >
              {loadingStudents ? (
                <CustomLoading />
              ) : filteredStudentList.length > 0 ? (
                filteredStudentList.map((s) => (
                  <div
                    key={s.student_uuid}
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--border-default)'
                    }}
                    onClick={() => handleStudentSelect(s)}
                  >
                    <div style={{ fontWeight: 'bold' }}>
                      {s.first_name} {s.last_name}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.email}</div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '8px', color: 'var(--text-muted)' }}>No students found</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={loadingStudents}
          className={`ml-2 bg-blue-600 text-white px-3 py-1 rounded h-[43px] ${loadingStudents ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {loadingStudents ? 'Loading...' : 'Search'}
        </button>
        <button
          onClick={() => {
            setFilters({ startDate: '', endDate: '', search: '', month: '' });
            setSelectedStudent(null);
            setShowDropdown(false);
            setStudentList([]);
            setLoadingStudents(false);
            if (typeof onClearResults === 'function') onClearResults();
          }}
          className="h-[30px]"
        >
          Clear
        </button>
      </div>
      {/* Summary */}
      <div className="pt-5">
        <p>
          <b>Teacher: </b> {teacherName}
        </p>

        {teacherPointsData ? (
          <div className="mt-5">
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
              <tbody>
                <tr>
                  <td className={tdStyle}>
                    <b>Month</b>
                  </td>
                  <td className={tdStyle}>
                    {new Date(teacherPointsData?.month).toLocaleDateString()}
                  </td>
                </tr>
                <tr>
                  <td className={tdStyle}>
                    <b>Beginning of the month</b>
                  </td>
                  <td className={tdStyle}>{teacherPointsData?.beginning_balance}</td>
                </tr>
                <tr>
                  <td className={tdStyle}>
                    <b>Added this month </b>
                  </td>
                  <td className={tdStyle}>{teacherPointsData?.points_added}</td>
                </tr>
                <tr>
                  <td className={tdStyle}>
                    <b>Added this month by care team</b>
                  </td>
                  <td className={tdStyle}>{teacherPointsData?.points_added_admin}</td>
                </tr>
                <tr>
                  <td className={tdStyle}>
                    <b>Consumed this month</b>
                  </td>
                  <td className={tdStyle}>{teacherPointsData?.points_consumed}</td>
                </tr>
                <tr>
                  <td className={tdStyle}>
                    <b>Current Balance</b>
                  </td>
                  <td className={`${tdStyle} font-bold`}>{teacherPointsData?.current_balance}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-5 text-center">
            <CustomLoading />
          </div>
        )}

        {/* Transactions */}
        {teacherPointsEntries?.length > 0 && (
          <div className="mt-[30px]">
            <h4>Transactions</h4>
            <table className="w-full mt-2" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--surface-sunken)' }}>
                  {['Date', 'Type', 'Name', 'Points', 'Reason'].map((h) => (
                    <th key={h} className={`${tdStyle} text-center`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teacherPointsEntries.map((e) => (
                  <tr key={e.id}>
                    <td className={tdStyle}>{new Date(e?.created_date).toLocaleString()}</td>
                    <td className={tdStyle}>{e?.type === 'cms_remove' ? 'Removed' : 'Added'}</td>
                    <td className={tdStyle}>{e?.cms_user_name || '-'}</td>
                    <td className={`${tdStyle} text-right`}>{e?.points}</td>
                    <td className={tdStyle}>{e?.cms_reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-3">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="border px-3 py-1 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Reason + Points Section */}
        <div className="mt-[24px]">
          <label>
            <b>Reason:</b>
          </label>
          <div className="flex items-center gap-3 mt-2">
            <button
              className="border px-3 py-2 rounded bg-[var(--surface-sunken)] hover:bg-[var(--border-default)] text-[var(--text-secondary)]"
              onClick={() => setShowReasonPopup(true)}
            >
              Enter Reason
            </button>
            <span>{pointsReason?.trim() ? pointsReason : 'No reason entered yet'}</span>
          </div>
        </div>

        <div className="mt-[24px]">
          <label>
            <b>Add / Remove points:</b>
          </label>
          <input
            className="w-[120px] p-1.5 rounded-[5px] ml-2.5 border border-[var(--border-default)]"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pointsValue}
            onChange={handlePointsChange}
            onPaste={handlePointsPaste}
            placeholder="e.g. 15"
          />
        </div>

        <div className="mt-[16px] space-x-3">
          <button onClick={handleAddPoints} className=" text-white px-3 py-1 rounded h-[41px]">
            Add Points
          </button>
          <button
            onClick={handleRemovePoints}
            className="hover:bg-[var(--surface-card)] hover:text-[var(--state-error)] hover:border hover:border-[var(--state-error)] bg-[var(--state-error)] border-0"
          >
            Remove Points
          </button>
        </div>

        {(validationError || errorMsg) && (
          <div className="mt-2.5 text-[var(--state-error)] font-bold">
            {validationError || errorMsg}
          </div>
        )}
      </div>
      {/* Reason Popup */}
      {showReasonPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] p-6 rounded-lg shadow-lg w-[400px]">
            <h3 className="font-bold mb-2">Enter Reason</h3>
            <textarea
              className="border w-full p-2 rounded mb-4"
              style={{ height: '200px', resize: 'vertical' }}
              value={localReason}
              onChange={(e) => setLocalReason(e.target.value)}
              placeholder="Enter reason for adding or removing points..."
            />
            <div className="flex justify-end gap-2">
              <button
                className="border px-3 py-2 rounded"
                onClick={() => setShowReasonPopup(false)}
              >
                Cancel
              </button>
              <button
                className="bg-[var(--state-info-solid)] text-white px-3 py-2 rounded"
                onClick={handleSaveReason}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrialSession;
